import FormattedMessage from 'I18n/components/FormattedMessage';
import { Spacer } from 'foundations-components/components/Spacer';
import { getButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { useSelector } from 'react-redux';
import ApiNameToUpgradeProductMap from 'self-service-api/constants/ApiNameToUpgradeProductMap';
import { TALK_TO_SALES_FALLBACK } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { APP } from '../../constants/AuthType';
import { BreezeIntelligenceCredits } from '../../constants/BreezeIntelligenceConstants';
import { MERCH_CARD } from '../../constants/tracking/TrackingSources';
import { TalkToSalesFallback } from '../../features/purchaseMotions/components/TalkToSalesFallback';
import { useCurrencyCodeUnitToDisplay, useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useAppSelector } from '../../reducers';
import { isAppAuthSelector } from '../../selectors/AuthSelectors';
import { isUngatedForSeatsSelector } from '../../selectors/GatingSelectors';
import { calculatedPriceSelector } from '../../selectors/PricingSelectors';
import { hasProductsInProductInventorySelector, productsFromProductInventoryQuantitySelector } from '../../selectors/ProductInventorySelectors';
import { includedQuantityBySkuIdSelector, productSelector, unitNameForSkuIdSelector } from '../../selectors/ProductsSelectors';
import { purchaseMotionsSelector } from '../../selectors/PurchaseMotionSelectors';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import PurchaseMotionButtonWrapper from '../common/purchaseMotions/PurchaseMotionButtonWrapper';
import ToInterstitialPurchaseMotionButtonWrapper from '../common/purchaseMotions/ToInterstitialPurchaseMotionButtonWrapper';
import ErrorBoundary from '../error-boundary/ErrorBoundary';
import BreezeTileDisclaimer from './BreezeTileDisclaimer';
import BreezeBusinessObjectives from './business-objectives/BreezeBusinessObjectives';
import { PriceDisplayBreeze } from './price-display/PriceDisplayBreeze';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const BreezeTile = ({
  apiName,
  screen,
  gridColumnStart,
  paddingInlineStart,
  paddingInlineEnd
}) => {
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const isPaid = useAppSelector(hasProductsInProductInventorySelector);
  const product = useAppSelector(state => {
    return productSelector(state, {
      apiName
    });
  });
  const currentSkuId = product ? product.currentSkuId : 0;
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const productConfigurations = [createProductConfig({
    skuId: currentSkuId
  })];
  const currencyCode = useDisplayedCurrencyCode();
  const currencyCodeUnit = useCurrencyCodeUnitToDisplay();
  const pricingInfo = useSelector(state => {
    return calculatedPriceSelector(state, {
      productConfigurations,
      queryParamTerm,
      displayedCurrencyCode: currencyCode
    });
  });
  const unitName = useAppSelector(state => unitNameForSkuIdSelector(state, {
    skuId: currentSkuId
  }));
  const includedUnits = useAppSelector(state => {
    var _includedQuantityBySk;
    return unitName ? (_includedQuantityBySk = includedQuantityBySkuIdSelector(state, {
      skuId: currentSkuId,
      unitName
    })) !== null && _includedQuantityBySk !== void 0 ? _includedQuantityBySk : 1 : 1;
  });
  const ownedQuantity = useAppSelector(state => productsFromProductInventoryQuantitySelector(state, {
    skuId: currentSkuId
  }));
  const isSeats = useAppSelector(isUngatedForSeatsSelector);
  const isPreSeats = !isSeats;
  const purchaseMotions = useAppSelector(state => purchaseMotionsSelector(state, {
    skuId: currentSkuId
  }));

  // Breeze Tile should only show for paid users
  if (!product || !isAppAuth || !isPaid) {
    return null;
  }

  // Normally, PMBW renders the ToInterstitial button for seats. There is an exception: for pre-seats users looking to buy the regular recurring breeze sku tiles (NOT `breeze-intelligence-credits-*-committed`), we want to take them to interstitial so they can adjust the quantity. PMBW can handle all other cases.
  const PurchaseMotionButtonWrapperToUse = isPreSeats && BreezeIntelligenceCredits.includes(apiName) ? ToInterstitialPurchaseMotionButtonWrapper : PurchaseMotionButtonWrapper;
  const textOverride = ownedQuantity > 0 ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "merchCards.breezeTile.buyAdditionalCredits"
  }) : undefined;
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PPUIGridItem, {
      "data-selenium-id": `${apiName}-merch-card`,
      gridColumnStart: gridColumnStart,
      gridRowStart: 1,
      paddingInlineStart: paddingInlineStart,
      paddingInlineEnd: paddingInlineEnd,
      paddingBlockStart: "800",
      children: /*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `merchCards.breezeTile.${apiName}.title`
        })
      })
    }), /*#__PURE__*/_jsxs(PPUIGridItem, {
      gridColumnStart: gridColumnStart,
      gridRowStart: 2,
      paddingInlineStart: paddingInlineStart,
      paddingInlineEnd: paddingInlineEnd,
      paddingBlockStart: "600",
      children: [/*#__PURE__*/_jsx(PriceDisplayBreeze, {
        pricingInfo: pricingInfo,
        includedUnits: includedUnits,
        seleniumId: apiName,
        currencyCode: currencyCode,
        currencyCodeUnit: currencyCodeUnit
      }), /*#__PURE__*/_jsx(BreezeTileDisclaimer, {
        apiName: apiName
      })]
    }), /*#__PURE__*/_jsx(PPUIGridItem, {
      gridColumnStart: gridColumnStart,
      gridRowStart: 3,
      paddingInlineStart: paddingInlineStart,
      paddingInlineEnd: paddingInlineEnd,
      paddingBlock: "600",
      children: /*#__PURE__*/_jsx(ErrorBoundary, {
        fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
          apiName: apiName,
          trackingSource: MERCH_CARD,
          screen: screen
        }),
        children: /*#__PURE__*/_jsx(PurchaseMotionButtonWrapperToUse, Object.assign({
          level: 1,
          purchaseMotions: purchaseMotions.length ? purchaseMotions : [TALK_TO_SALES_FALLBACK],
          trackingSource: MERCH_CARD,
          upgradeData: {
            apiName,
            app: APP,
            screen,
            uniqueId: `${apiName}-${MERCH_CARD}`,
            upgradeProduct: ApiNameToUpgradeProductMap[apiName]
          },
          use: "primary",
          productConfigurations: [createProductConfig({
            skuId: currentSkuId,
            quantity: Math.max(ownedQuantity, 1)
          })]
        }, ctaVariantProps, {
          _textOverride: textOverride,
          purchaseMotionLocation: 'MERCH_CARD',
          children: textOverride
        }))
      })
    }), /*#__PURE__*/_jsx(PPUIGridItem, {
      gridColumnStart: gridColumnStart,
      gridRowStart: 4,
      paddingInlineStart: paddingInlineStart,
      paddingInlineEnd: paddingInlineEnd,
      children: /*#__PURE__*/_jsx(BreezeBusinessObjectives, {
        apiName: apiName
      })
    }), /*#__PURE__*/_jsx(Spacer, {
      space: "800"
    })]
  });
};
export default BreezeTile;